import { useEffect, useRef } from 'react';
import { FileText } from 'lucide-react';
import type { Source } from '@/data/fixtures';
import { PDFViewer } from './viewers/by-type/PDFViewer';
import { VideoViewer } from './viewers/by-type/VideoViewer';
import { AudioViewer } from './viewers/by-type/AudioViewer';
import { ImageViewer } from './viewers/by-type/ImageViewer';
import { LinkViewer } from './viewers/by-type/LinkViewer';
import { TextViewer } from './viewers/by-type/TextViewer';
import { MarkdownViewer } from './viewers/by-type/MarkdownViewer';
import { CSVViewer } from './viewers/by-type/CSVViewer';
import { HTMLViewer } from './viewers/by-type/HTMLViewer';
import { PlainTextViewer } from './viewers/by-type/PlainTextViewer';
import { DocxViewer } from './viewers/by-type/DocxViewer';
import { UnsupportedFileViewer } from './viewers/by-type/UnsupportedFileViewer';
import { pickViewer } from './pickViewer';

interface SourceViewerProps {
  selectedSource: Source | null;
  /**
   * False when the lesson has no sources at all. The empty state then says so,
   * instead of "select a source from the panel" — a panel with nothing in it
   * (`Layout/Lesson/Viewer/SourceViewer/NoSources`). Unknown (undefined) keeps
   * the select-a-source copy.
   */
  hasSources?: boolean;
  onProgressUpdate?: (sourceId: string) => void;
}

export function SourceViewer({
  selectedSource,
  hasSources,
  onProgressUpdate,
}: SourceViewerProps) {
  // Keep the latest callback in a ref so the "mark viewed" effect below can
  // depend ONLY on `selectedSource`. Parents (LessonViewer / ReferenceViewer)
  // recreate this callback on every render, and it ultimately calls
  // setState with a fresh object each time - depending on its identity here
  // would re-run the effect -> setState -> re-render -> new identity ->
  // re-run, an infinite loop ("Maximum update depth exceeded").
  const onProgressUpdateRef = useRef(onProgressUpdate);
  useEffect(() => {
    onProgressUpdateRef.current = onProgressUpdate;
  });

  useEffect(() => {
    if (selectedSource) {
      onProgressUpdateRef.current?.(selectedSource.id);
    }
  }, [selectedSource]);

  const handleDownload = (_source: Source) => {
    // Legacy hook for the older viewers - kept so VideoViewer/AudioViewer
    // can stay untouched. New viewers fetch their own presigned URLs.
  };

  const handleOpenExternal = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const renderSourceContent = (source: Source) => {
    const kind = pickViewer(source);
    switch (kind) {
      case 'pdf':
        return (
          <PDFViewer
            source={source}
            onDownload={handleDownload}
            onOpenExternal={handleOpenExternal}
          />
        );
      case 'image':
        return <ImageViewer source={source} onDownload={handleDownload} />;
      case 'video':
        return (
          <VideoViewer source={source} onOpenExternal={handleOpenExternal} />
        );
      case 'audio':
        return <AudioViewer source={source} onDownload={handleDownload} />;
      case 'link':
        return (
          <LinkViewer source={source} onOpenExternal={handleOpenExternal} />
        );
      case 'text':
        return <TextViewer source={source} onDownload={handleDownload} />;
      case 'markdown':
        return <MarkdownViewer source={source} />;
      case 'csv':
        return <CSVViewer source={source} />;
      case 'html':
        return <HTMLViewer source={source} />;
      case 'plaintext':
        return <PlainTextViewer source={source} />;
      case 'docx':
        return <DocxViewer source={source} />;
      case 'unsupported':
      default:
        return <UnsupportedFileViewer source={source} />;
    }
  };

  if (!selectedSource) {
    return (
      // Matches the OnDemandWrapper "generate" CTA pattern used by Quiz /
      // Glossary / Extras: fade-up animation + blue gradient icon circle.
      // `key` ensures the animation replays when this empty state appears
      // after switching tabs.
      <div
        key='no-source-selected'
        className='flex h-full animate-fade-in flex-col items-center justify-center px-4 py-16'
      >
        <div className='mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-brand-50 dark:bg-muted'>
          <FileText className='h-7 w-7 text-brand-500/80 dark:text-primary' />
        </div>
        <h3 className='mb-2 text-lg font-medium text-foreground'>
          {hasSources === false ? 'No sources yet' : 'No Source Selected'}
        </h3>
        <p className='max-w-sm text-center text-sm text-muted-foreground'>
          {/* "Sources", not "the panel": below `lg` there is no panel beside
              the viewer, only the top bar's Sources button and its sheet. */}
          {hasSources === false
            ? 'Add a PDF, link or notes from Sources, and it opens here.'
            : 'Pick a source from Sources to view it here.'}
        </p>
      </div>
    );
  }

  return (
    <div className='flex h-full flex-col'>
      {/* Main content area - header stripped so the source gets the full
          panel. Source name + type + size are already shown in the
          SourcesPanel list above. */}
      <div className='flex-1 overflow-hidden'>
        {renderSourceContent(selectedSource)}
      </div>
    </div>
  );
}
